import { useVaultStore } from '../../store/vaultStore';
import { newItemBlockedByCap } from '../../services/vaultCloudCap';
import {
  shouldShowDestinations,
  PERSONAL_DESTINATION,
  parseDestination,
  buildFolderOptions,
  canWriteTeamRoot,
  defaultTeamFolderValue,
  saveTargetFor,
} from '../../utils/vaultDestinations';
import teamVaultService, { isTeamItemKey } from '../../services/teamVaultService';
import { refreshTeamVault } from '../../store/teamVaultStore';
import { DB_DEFAULT_PORTS, DEFAULT_PORTS, isValid, buildData, buildTeamData } from './vaultItemData';

/** Folder / destination / engine / protocol changes, custom fields and submit for the vault item modal. */
export function useVaultItemForm({
  values,
  item,
  type,
  destination,
  destinations,
  byTeam,
  teamContext,
  onClose,
  ephemeralOption,
  setValue,
  setValues,
  saving,
  setSaving,
  setCapDraft,
  setError,
  setDestination,
  teamFolderId,
  setTeamFolderId,
  setCreatingFolder,
  NEW_FOLDER,
}) {
  const changeFolder = (choice) => {
    if (choice === NEW_FOLDER) {
      setCreatingFolder(true);
      setValue('folder', '');
      return;
    }
    setCreatingFolder(false);
    setValue('folder', choice);
  };

  // ── destination (team vault) ──────────────────────────────────────────────
  // Editing keeps the item where it is: an existing item's vault is not a form
  // field, because moving one across is a copy between two different encryption
  // models, not a re-file.
  // A value no `buildDestinations` result can produce, so it can never collide
  // with a real personal/team destination.
  const EPHEMERAL_DESTINATION = '__ephemeral__';
  const offerEphemeral = !item && !!ephemeralOption?.onSave;
  const isEphemeral = offerEphemeral && destination === EPHEMERAL_DESTINATION;
  // The picker appears when there is somewhere ELSE to save — and "nowhere" now
  // counts as somewhere else, so a solo user sees it too once a caller offers
  // the option.
  const showDestination = !item && (shouldShowDestinations(destinations) || offerEphemeral);
  const activeDestination = teamContext
    ? `team:${teamContext.teamId}`
    : showDestination
      ? destination
      : PERSONAL_DESTINATION;
  const parsedDestination = parseDestination(activeDestination);
  const isTeamDestination = parsedDestination.kind === 'team';
  const teamFolderOptions = buildFolderOptions(activeDestination, { personalFolders: [], byTeam });
  const teamRootWritable = canWriteTeamRoot(activeDestination, byTeam);

  /**
   * Changing destination re-derives the folder list from the NEW destination
   * and clears whichever folder field the old one owned. Carrying a personal
   * folder name over to a team (or a team folder id back to a personal item)
   * would either write a meaningless value into the encrypted body or file the
   * item somewhere the user did not look at.
   */
  const changeDestination = (choice) => {
    setDestination(choice);
    setCreatingFolder(false);
    setValue('folder', '');
    // NOT a bare '': the root option is omitted when the root is unwritable, so
    // '' would be a selection the picker does not offer and the server refuses.
    // `defaultTeamFolderValue` returns the first writable folder in that case.
    setTeamFolderId(defaultTeamFolderValue(choice, byTeam));
  };

  const changeEngine = (engine) => {
    setValues((v) => {
      const wasDefault = v.port === '' || v.port === String(DB_DEFAULT_PORTS[v.engine] || '');
      return { ...v, engine, port: wasDefault ? String(DB_DEFAULT_PORTS[engine]) : v.port };
    });
  };

  const changeProtocol = (protocol) => {
    setValues((v) => {
      const wasDefault = v.port === '' || v.port === String(DEFAULT_PORTS[v.protocol]);
      return {
        ...v,
        protocol,
        port: wasDefault ? String(DEFAULT_PORTS[protocol]) : v.port,
        // FTP / FTPS log in with a password only (ftp.rs) — card 50122def.
        authMethod: protocol === 'sftp' ? v.authMethod : 'password',
      };
    });
  };

  const updateCustomField = (idx, patch) =>
    setValues((v) => ({
      ...v,
      fields: v.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  const addCustomField = () =>
    setValues((v) => ({ ...v, fields: [...v.fields, { k: '', v: '', secret: false }] }));
  const removeCustomField = (idx) =>
    setValues((v) => ({ ...v, fields: v.fields.filter((_, i) => i !== idx) }));

  const canSave = type != null && isValid(type, values);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Nowhere to write, so none of the paths below apply — hand the values to
      // the caller and close. FIRST, so no vault or team call can be reached
      // with this destination selected.
      if (isEphemeral) {
        await ephemeralOption.onSave({ type, name: values.name.trim(), values });
        onClose();
        return;
      }

      // Editing an item that already lives in a team vault. `version` is
      // REQUIRED by the API: omitting it is a 422 and a stale one is a 409, so
      // two admins editing one production password get a conflict to resolve
      // rather than silent last-write-wins.
      if (teamContext?.teamId != null && teamContext?.itemId != null) {
        await teamVaultService.updateItem(teamContext.teamId, teamContext.itemId, {
          version: teamContext.version,
          name: values.name.trim(),
          itemType: type,
          folderId: teamFolderId === '' ? null : teamFolderId,
          data: buildTeamData(type, values),
        });
        await refreshTeamVault(teamContext.teamId);
        onClose();
        return;
      }

      // Fail CLOSED. Below this line the save goes to `vault_put_item`, which
      // writes the PERSONAL table — and a `team:<teamId>:<itemId>` id there is
      // a write against an id that cannot exist in it. Unreachable from the
      // three call sites (each sets `teamContext` alongside a team id); this is
      // the guard for the fourth one somebody adds.
      if (isTeamItemKey(item?.id)) {
        throw new Error('team_vault_missing_context');
      }

      const target = saveTargetFor(activeDestination, teamFolderId);
      if (target.kind === 'team') {
        await teamVaultService.createItem(target.teamId, {
          itemType: type,
          name: values.name.trim(),
          folderId: target.folderId,
          data: buildTeamData(type, values),
        });
        await refreshTeamVault(target.teamId);
        onClose();
        return;
      }

      // Card U1: a NEW item into a full free cloud vault asks first (upgrade,
      // or keep it on this device only). Editing never asks.
      if (!item?.id) {
        const cap = await newItemBlockedByCap();
        if (cap) {
          setCapDraft({ cap, item: { type, name: values.name.trim(), data: buildData(type, values) } });
          setSaving(false);
          return;
        }
      }
      await useVaultStore.getState().putItem({
        ...(item?.id ? { id: item.id } : {}),
        type,
        name: values.name.trim(),
        ...(item ? { sync: item.sync, favorite: item.favorite } : {}),
        data: buildData(type, values),
      });
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
      setSaving(false);
    }
  };

  return {
    changeFolder,
    EPHEMERAL_DESTINATION,
    offerEphemeral,
    isEphemeral,
    showDestination,
    isTeamDestination,
    teamFolderOptions,
    teamRootWritable,
    changeDestination,
    changeEngine,
    changeProtocol,
    updateCustomField,
    addCustomField,
    removeCustomField,
    canSave,
    handleSubmit,
  };
}
